import { makeAutoObservable } from "mobx";
import React from "react";
import SearchState, { API_BASE_URL, DISABLE_CACHEING } from "../../SearchState";
import { categories } from "@shared/constants";
import TrackToUploadState from "./TrackToUpload/TrackToUploadState";
import retryFetch from "../../utils/retryFetch";
import TrackToUpload from "./TrackToUpload/TrackToUpload";
import { globalAudioContext } from "../MusicTrack/MusicTrackState";

/** @typedef {import("@shared/typedefs").Category} Category */
/** @typedef {import("@shared/musicEndpointTypes").SavedPlaylistPost} SavedPlaylistPost */
/** @typedef {import("@shared/musicEndpointTypes").SavedSearchPostResponse} SavedSearchPostResponse */
/** @typedef {import("@shared/musicEndpointTypes").TrackPostResponse} TrackPostResponse */

export default class UploadTrackState {
  /** @type {SearchState} */
  searchState;

  /** @type {Category | null} */
  category;

  nextName = "";

  success = false;

  /** @type {Array<TrackToUploadState>} */
  fileList = [];

  /** @type {Array<any>} */
  acceptedFiles = [];

  /** @type {Array<any>} */
  filesAlreadyInQueue = [];

  /**
   * @param {SearchState} searchState
   */
  constructor(searchState) {
    makeAutoObservable(this, {}, { enforceActions: "observed" });
    this.searchState = searchState;
    this.setCategory(categories[0]);
    (() => {
      // this.fileList = [new TrackToUploadState(this)];
    })();
  }

  /**
   * Resets the upload state to initial values
   * @action
   */
  reset = () => {
    this.success = false;
    this.fileList = [];
    this.filesAlreadyInQueue = [];
    this.acceptedFiles = [];
  };

  /**
   * Closes upload window and searches for the uploaded track
   * @action
   */
  searchForUpload = () => {
    this.searchState.hideUploadWindow();
    this.searchState.doKeywordSearch(this.nextName);
  };

  /**
   * Sets the selected category for uploads
   * @param {Category} category
   * @action
   */
  setCategory = (category) => {
    this.category = category;
  };

  upload = () => {
    if (this.category != null) {
      const category = this.category;

      this.fileList = this.fileList.filter(
        (file) => file.mp3File != null && file.wavFile != null,
      );
      this.filesAlreadyInQueue = this.fileList.map((file) => file.trackName);

      Promise.all(this.fileList.map((f) => f.upload(category))).then(
        (results) => {
          const ids = results.filter((r) => r.success).map((r) => r.id);
          if (ids.length === 0) {
            return;
          }

          const saveName = `Automated playlist from upload created on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`;

          /** @type {SavedPlaylistPost} */ const savedPlaylist = {
            tracks: ids,
            name: saveName,
          };

          retryFetch(`${API_BASE_URL}/api/v1/saved_playlist`, {
            method: "POST",
            cache: DISABLE_CACHEING ? "reload" : "default",
            headers: new Headers({ "content-type": "application/json" }),
            body: JSON.stringify(savedPlaylist),
            mode: "cors",
            withCredentials: true,
            credentials: "include",
          })
            .then((r) => r.json())
            .then((r) => {
              if (r.id === -1) {
                alert("Failed to save the playlist. Please try again.");
                return;
              }
              this.searchState.myAccountState.refreshSavedTracks();
              this.close();
              if (r.id > -1) {
                const url = `/en/favorites/${r.id}`;
                window.history.pushState({}, "", url);
                this.searchState.refreshSearchFromURL();
              }
            });
        },
      );
    }
  };

  /**
   * @param {TrackToUploadState} trackToUpload
   * @action
   */
  remove = (trackToUpload) => {
    const index = this.fileList.indexOf(trackToUpload);
    if (index !== -1) this.fileList.splice(index, 1);
  };

  addTrack = () => {
    // unused now that the batch upload is in place
    this.fileList.push(new TrackToUploadState(this));
  };

  addTrackViaDropzone = (acceptedFiles) => {
    acceptedFiles.map((track, i) => {
      const trackName = track.name.replace(".mp3", "").replace(".wav", "");

      if (this.fileList.length > 0) {
        this.fileList.forEach((result) => {
          const exists = this.filesAlreadyInQueue.indexOf(trackName);

          if (exists === -1) {
            // console.log('creating new instance of track to upload');
            const newTrack = new TrackToUploadState(track);

            if (track.type == "audio/mp3" || track.type == "audio/mpeg") {
              newTrack.setMp3(track);
            }
            if (track.type == "audio/wav") {
              newTrack.setWav(track);
            }
            this.fileList.push(newTrack);
            this.filesAlreadyInQueue.push(trackName);
          } else if (result.mp3File || result.wavFile) {
            if (trackName == result.trackName) {
              if (track.type == "audio/mp3" || track.type == "audio/mpeg") {
                result.setMp3(track);
              }
              if (track.type == "audio/wav") {
                result.setWav(track);
              }
            }
          } else {
            const newTrack = new TrackToUploadState(track);
            if (track.type == "audio/mp3" || track.type == "audio/mpeg") {
              result.setMp3(track);
            }
            if (track.type == "audio/wav") {
              result.setWav(track);
            }
            this.fileList.push(newTrack);
          }
        });
      } else {
        const newTrack = new TrackToUploadState(track);
        // console.log(track.type);

        if (track.type == "audio/mp3" || track.type == "audio/mpeg") {
          newTrack.setMp3(track);
        } else if (track.type == "audio/wav") {
          newTrack.setWav(track);
        }
        this.filesAlreadyInQueue.push(trackName);
        this.fileList.push(newTrack);
      }
    });

    this.filesAlreadyInQueue.filter((item, i, ar) => ar.indexOf(item) === i);

    console.log(this.filesAlreadyInQueue);
    // console.log(this.fileList);
  };

  close = () => {
    this.searchState.hideUploadWindow();
  };
}
